import {
    DndContext, DragOverlay, PointerSensor,
    closestCorners,
    useSensor, useSensors,
} from "@dnd-kit/core";
import { RiArrowLeftLine, RiArrowRightLine, RiRestartLine } from "@remixicon/react";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { DashboardTicketDisplay, DashboardView } from "../../../__generated__/api";
import { TicketItem } from "../../../contexts/ticket-state";
import { useTicketState } from "../../../contexts/ticket-state/TicketState";
import { ActionContext } from "../../../contexts/ticket-state/action-registry";

import EndChat from "../../agent/end-chat/EndChat";
import Hold from "../../agent/hold/Hold";
import Ignore from "../../agent/ignore/Ignore";
import MarkRead from "../../agent/mark-read/MarkRead";
import MarkUnread from "../../agent/mark-unread/MarkUnread";
import SetTicketState from "../../agent/set-ticket-state/SetTicketState";
import Snooze from "../../agent/snooze/Snooze";
import Transfer from "../../agent/transfer/Transfer";
import SendMessages from "../../agent/send-messages/SendMessages";
import Move from "../../agent/move/Move";
import AddParticipants from "../../agent/add-participants/AddParticipants";
import BulkActionsMenu from "./bulk-action/BulkActions";

import Loading from "../../loading/Loading";

import { Paths, useParams } from "../../../contexts/Routing";
import BaseListener from "../../../contexts/ticket-state/base-listener";
import { debounce } from "../../../debouncer";
import StartConversation from "../../start-conversation/StartConversation";
import Button from "../../theme/button/Button";
import Styles from "./Board.module.scss";
import Card from "./card/Card";
import Column from "./column/Column";
import NoResults from "./no-results/NoResults";
import SearchColumn from "./column/SearchColumn";
import EasterEgg from "../../agent/easter/EasterEgg";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";

function findRanges(columns: {isVisible: boolean}[]) {
    let leftVisibleStart = -1;
    let leftVisibleEnd = -1;

    for (let index = 0; index < columns.length; index++) {
        if (columns[index].isVisible) {
            if (leftVisibleStart < 0) {
                leftVisibleStart = index;
            }
            leftVisibleEnd = index;
        }
    }

    return {
        leftEnd: leftVisibleStart > 0 ? leftVisibleStart - 1 : -1,
        rightStart: leftVisibleEnd >= 0 && leftVisibleEnd < columns.length - 1 ? leftVisibleEnd + 1 : -1,
    };
}
export default function Board() {
    const ticketContext = useTicketState();

    const [draggingItem, setDraggingItem] = useState<TicketItem>();
    const { chatId } = useParams(Paths.AGENT_CHAT);

    const listener = BaseListener.useListener();

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
    );

    const view = ticketContext.currentDashboard?.settings?.view ?? DashboardView.Kanban;
    const ticketDisplay = ticketContext.currentDashboard?.settings?.ticketDisplay ?? DashboardTicketDisplay.SideRight;

    const columns = ticketContext.dashboardView?.getData();
    const searchContext = ticketContext.searchState.useSearchContext();

    const shouldShowResultHelpers = searchContext.isSearching
        && columns?.isNotEmpty
        && ticketContext.currentDashboard.settings?.view === DashboardView.Kanban
        && searchContext.search;

    const boardRef = useRef<HTMLDivElement>(null);
    const searchColumnRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!boardRef.current || !shouldShowResultHelpers) {
            return undefined;
        }

        const debouncer = debounce(() => listener.updateListners(), 500);
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => entry.target.classList.toggle("visible", entry.isIntersecting));
            debouncer();
        }, {
            threshold: 0.5,
        });

        boardRef.current.querySelectorAll(".column").forEach(column => observer.observe(column));
        if (searchColumnRef.current) {
            observer.observe(searchColumnRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [boardRef.current, shouldShowResultHelpers, searchContext.search, searchColumnRef.current]);

    let invisibleLeft = {
        count: 0,
        index: -1,
    };
    let invisibleRight = {
        count: 0,
        index: -1,
    };

    if (shouldShowResultHelpers) {
        const { leftEnd, rightStart } = findRanges(columns);
        invisibleLeft = {
            count: leftEnd >= 0 ? columns.limit(leftEnd + 1).sum(row => row.ticketCount) : 0,
            index: leftEnd - 1,
        };

        const rightCount = rightStart >= 0 ? columns.skip(rightStart).sum(row => row.ticketCount) : 0;
        invisibleRight = {
            count: rightCount,
            index: rightCount > 0 && rightStart >= 0 ? rightStart + 1 : -1,
        };

        if (!searchColumnRef.current?.classList.contains("visible")) {
            invisibleRight.count += searchContext.numberRemoteResults;
        }
    }

    const createScrollTo = (columnIndex: number) => () => {
        if (columnIndex < 0 && searchColumnRef.current) {
            searchColumnRef.current.scrollIntoView({
                behavior: "smooth",
            });
            return;
        }
        columns?.[columnIndex]?.scrollIntoView();
        setTimeout(() => {
            listener.updateListners();
        }, 500);
    };

    const isEmpty = columns?.isNotEmpty
        && (ticketContext.currentDashboard?.settings?.view === DashboardView.Compact || searchContext.isSearching)
        && (!searchContext.isLoadingRemote && searchContext.numberRemoteResults <= 0)
        && ticketContext.dashboardView?.getData().every(column => column.groups.isEmpty);
    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            autoScroll={{
                acceleration: 0.1,
            }}
            onDragStart={event => {
                const ticket = ticketContext.tickets?.get(event.active.id as string);
                if (!ticket) {
                    return;
                }

                if (!ticket.isChecked) {
                    ticketContext.selectionState.pushHistory();
                }

                ticketContext.selectionState.addSelection(ticket);

                // ticket.setIsChecked(true);
                setDraggingItem(ticket);
            }}
            onDragEnd={event => {
                const newStateId = event.over?.id as string | undefined;
                if (newStateId) {
                    const newColumn = ticketContext.dashboardView?.getColumn(newStateId);
                    if (!newColumn) {
                        return;
                    }

                    const selection = ticketContext.selectionState.current;
                    ticketContext.dashboardView?.moveTickets(selection, newColumn, ActionContext.BULK_ACTION, () => {
                        selection.firstElement?.focusViewWithUpdate();
                        ticketContext.selectionState.clear();
                    });
                }

                LoggingHandler.instance.metric([LoggingHandler.Metrics.AGENT, "card", "dragged"]);

                ticketContext.selectionState.popHistory();
                setDraggingItem(undefined);
            }}
        >
            <div
                data-tour-panel="board-main"
                className={classNames(
                    Styles["board-container"],
                    (chatId && chatId !== "new") ? Styles[`board-open-${ticketDisplay.toLowerCase()}`] : "",
                )}
            >
                <DragOverlay>
                    {draggingItem && (
                        <Card ticketItem={draggingItem} isDragging />
                    )}
                </DragOverlay>

                {ticketContext.isChatLoading ? (
                    <Loading />
                ) : (
                    <div
                        ref={boardRef}
                        className={classNames(
                            Styles.board,
                            Styles[`board-${view.toLowerCase()}`],
                        )}
                    >
                        {!isEmpty && columns?.map((ticketState, index) => (
                            <Column
                                key={ticketContext.currentDashboard.id + ticketState.ticketState.id}
                                ticketState={ticketState}
                                columnIndex={index}
                                draggingItem={draggingItem}
                            />
                        ))}

                        {searchContext.isSearching
                            && searchContext.shouldSearchArchived
                            && <SearchColumn ref={searchColumnRef} />}

                        {isEmpty && (
                            <NoResults
                                title="No Results"
                                text={"Try adjusting your filters to find more tickets. "
                                + "(Or maybe, this just means your job here is done)"}
                                resetButtonText={searchContext.search ? "Clear Search" : "Reset Filters"}
                                onClear={() => {
                                    if (searchContext.isSearching) {
                                        ticketContext.searchState.setSearchQuery("");
                                    } else {
                                        ticketContext.currentDashboardSettings = {
                                            ...ticketContext.currentDashboardSettings,
                                            filters: {},
                                        };
                                    }
                                }}
                                resetIcon={<RiRestartLine />}
                            />
                        )}
                    </div>
                )}

                {invisibleLeft.count > 0 && (
                    <Button
                        variant="outline-blue"
                        onClick={createScrollTo(invisibleLeft.index)}
                        className={classNames(Styles["search-more-results"], Styles["search-more-results-left"])}
                    >
                        <RiArrowLeftLine />
                        {" "}
                        {invisibleLeft.count}
                        {" "}
                        more
                        {" "}
                        {invisibleLeft.count === 1 ? "result" : "results"}
                    </Button>
                )}

                {invisibleRight.count > 0 && (

                    <Button
                        variant="outline-blue"
                        onClick={createScrollTo(invisibleRight.index)}
                        className={classNames(Styles["search-more-results"], Styles["search-more-results-right"])}
                    >
                        {invisibleRight.count}
                        {" "}
                        more
                        {" "}
                        {invisibleRight.count === 1 ? "result" : "results"}
                        {" "}
                        <RiArrowRightLine />
                    </Button>
                )}
            </div>
            <div className={Styles.actions}>
                <BulkActionsMenu />
                <SendMessages />
                <Move />
                <AddParticipants />
                <MarkRead />
                <MarkUnread />
                <Snooze />
                <Transfer />
                <Ignore />
                <EndChat />
                <SetTicketState />
                <Hold />
                <StartConversation />
                <EasterEgg />
            </div>

        </DndContext>
    );
}
